import { lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import {
  RequireAnonymous,
  RequireAuth,
  RequireRestricted,
  RequireVerifiedOrganization,
} from './guards';
import { LandingRedirect } from './LandingRedirect';
import { legacyRedirects } from './legacyRedirects';
import { moduleRouteGroups } from './moduleRoutes';
import { paths } from './paths';

/*
 * The shell's own skeleton, and nothing else: the public pages, the wizard, the
 * four pages that belong to no module, and then every module's routes generated
 * from `src/modules/registry.ts`. A module's pages and their per-screen
 * permission guards live in its manifest, so this file does not grow when a
 * module is added.
 */

// Every page is code-split: the sign-in screen should not ship the data grid.
const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/features/auth/RegisterPage'));
const VerifyPhonePage = lazy(() => import('@/features/auth/VerifyPhonePage'));
const AcceptInvitePage = lazy(() => import('@/features/auth/AcceptInvitePage'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/features/auth/ResetPasswordPage'));

const OnboardingPage = lazy(() => import('@/features/onboarding/OnboardingPage'));
const ModulesPage = lazy(() => import('@/features/modules/ModulesPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const NotificationsPage = lazy(() => import('@/features/notifications/NotificationsPage'));
const ProfilePage = lazy(() => import('@/features/account/ProfilePage'));
const SettingsPage = lazy(() => import('@/features/account/SettingsPage'));
const NotFoundPage = lazy(() => import('@/features/errors/NotFoundPage'));
const ForbiddenPage = lazy(() => import('@/features/errors/ForbiddenPage'));

export const router = createBrowserRouter([
  {
    element: <RequireAnonymous />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { path: paths.login, element: <LoginPage /> },
          { path: paths.register, element: <RegisterPage /> },
          { path: paths.verifyPhone, element: <VerifyPhonePage /> },
          { path: paths.acceptInvite, element: <AcceptInvitePage /> },
          { path: paths.forgotPassword, element: <ForgotPasswordPage /> },
          { path: paths.resetPassword, element: <ResetPasswordPage /> },
        ],
      },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      {
        // Outside the shell: the wizard has its own chrome, and the console's
        // navigation would only offer doors that are all locked.
        element: <RequireRestricted />,
        children: [{ path: paths.onboarding, element: <OnboardingPage /> }],
      },
      {
        element: <RequireVerifiedOrganization />,
        children: [
          // Outside the shell as well: a printed sheet is the whole page, so the
          // browser prints paper and not a sidebar.
          ...moduleRouteGroups('print'),
          {
            element: <DashboardLayout />,
            children: [
              // Signing in lands on the apps grid — or straight in the module,
              // for someone who has exactly one. The dashboard's figures are one
              // click away in the module switcher.
              { path: paths.root, element: <LandingRedirect /> },
              { path: paths.modules, element: <ModulesPage /> },
              { path: paths.dashboard, element: <DashboardPage /> },
              { path: paths.notifications, element: <NotificationsPage /> },
              { path: paths.profile, element: <ProfilePage /> },
              { path: paths.settings, element: <SettingsPage /> },
              { path: paths.forbidden, element: <ForbiddenPage /> },

              ...moduleRouteGroups('shell'),

              // Bookmarks and pasted links from before the prefixes. Time-boxed
              // — the file says when to delete it.
              ...legacyRedirects(),

              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
]);
